// ============================================================================
// JARVISTRAVEL - BUDGET status scale (shared helpers)
// The 7 predictive trend states from the Budget Engine spec, mapped to a
// calm → alarm visual tier. Status is always conveyed with a label + icon, not
// color alone (a11y). Single source of truth so every budget surface reads the
// same. Mirrors the FI scale pattern in utils/fi.ts.
// ============================================================================

import { formatCurrency } from '@/app/utils';
import type { Budget, Transaction } from '@/app/types';

export type BudgetStatus =
  | 'healthy_pace'
  | 'elevated_pace'
  | 'trending_over'
  | 'exhaustion_risk'
  | 'budget_exhausted'
  | 'hard_cap_conflict'
  | 'low_confidence';

export interface BudgetTone {
  text: string;
  bg: string;
  solid: string;
  border: string;
  ring: string;
}

type Tier = 'calm' | 'caution' | 'over' | 'alarm' | 'neutral';

// Tiers reuse the semantic + fatigue tokens so budget and fatigue feel like one
// system: calm green → caution amber → over (warmer amber) → alarm red.
const TIER_TONE: Record<Tier, BudgetTone> = {
  calm:    { text: 'text-success',     bg: 'bg-success/10',     solid: 'bg-success',     border: 'border-success/30',     ring: 'ring-success/40' },
  caution: { text: 'text-warning',     bg: 'bg-warning/10',     solid: 'bg-warning',     border: 'border-warning/30',     ring: 'ring-warning/40' },
  over:    { text: 'text-fi-elevated', bg: 'bg-fi-elevated/10', solid: 'bg-fi-elevated', border: 'border-fi-elevated/30', ring: 'ring-fi-elevated/40' },
  alarm:   { text: 'text-danger',      bg: 'bg-danger/10',      solid: 'bg-danger',      border: 'border-danger/30',      ring: 'ring-danger/40' },
  neutral: { text: 'text-muted',       bg: 'bg-surface-2',      solid: 'bg-muted',       border: 'border-border',         ring: 'ring-border' },
};

const STATUS_META: Record<BudgetStatus, { label: string; tier: Tier }> = {
  healthy_pace:     { label: 'Healthy pace',   tier: 'calm' },
  elevated_pace:    { label: 'Elevated pace',  tier: 'caution' },
  trending_over:    { label: 'Trending over',  tier: 'over' },
  exhaustion_risk:  { label: 'Exhaustion risk', tier: 'alarm' },
  budget_exhausted: { label: 'Budget used up', tier: 'alarm' },
  hard_cap_conflict:{ label: 'Over hard cap',  tier: 'alarm' },
  // Too little of the plan is priced to project from, so the projection leans
  // on the allocations. Said as a traveller would say it (JAR-2250).
  low_confidence:   { label: 'Rough estimate', tier: 'neutral' },
};

export const budgetTone = (status: BudgetStatus): BudgetTone => TIER_TONE[STATUS_META[status].tier];
export const budgetStatusLabel = (status: BudgetStatus): string => STATUS_META[status].label;

/** Is this a state the user should act on (vs. calm / informational)? */
export const budgetStatusIsActionable = (status: BudgetStatus): boolean =>
  status === 'trending_over' || status === 'exhaustion_risk' || status === 'budget_exhausted' || status === 'hard_cap_conflict';

/** Confidence score → human label (spec §7). */
export const confidenceLabel = (score: number): 'low' | 'medium' | 'high' =>
  score >= 0.75 ? 'high' : score >= 0.45 ? 'medium' : 'low';

// ── Item-level cost state (Cost Chip) ──────────────────────────────────────
// Grows with the engine; FE-1 derives free/estimated/booked from the itinerary.
export type CostState = 'free' | 'estimated' | 'booked' | 'actual' | 'excluded';

/** An amount the expense forms take: above zero, and finite in cents, since a
 *  bank charge's change is sent in cents and Infinity goes out as null. One rule
 *  for the add button and the add, commit and change checks. A number field
 *  already empties what it can't parse, "1e309" included, but the rule doesn't
 *  rely on the field. */
export const isExpenseAmount = (n: number): boolean => Number.isFinite(n * 100) && n > 0;

/** A budget is set once its total is above zero. A trip with no budget arrives
 *  with a $0 total, and a negative total can't be spent against (its share spent
 *  would come out negative), so both read as not set. */
export const isBudgetSet = (budget: Pick<Budget, 'total'>): boolean => budget.total > 0;

/** The header's budget button, as a screen reader hears it: what's left, in the
 *  trip's own currency, or that no budget is set. */
export function budgetButtonLabel(budget: Pick<Budget, 'total' | 'remaining' | 'currency'>): string {
  return isBudgetSet(budget)
    ? `Budget: ${formatCurrency(budget.remaining, budget.currency)} remaining`
    : 'Budget: not set yet';
}

/**
 * Whether a stop carries a price at all. Absent (undefined) is free — no amount,
 * the state a cleared cost now saves as (JAR-2533). A stored 0 is a real price of
 * zero, not free, and reads as $0.00 wherever a cost is shown; `stopFields` keeps
 * a typed 0 as 0 and a blank as undefined, so the two are distinct by the time a
 * reader sees them. One predicate so the cost readers (here, tripStats,
 * BudgetManager) can't drift again on what a 0 means.
 *
 * A non-finite cost (NaN, ±Infinity) or a negative one is NOT priced: a stop's
 * quoted cost is never negative — the core CHECK and the FE both refuse one
 * (JAR-2533) — and a non-finite value is never a real amount. Counting either would
 * skew a planned total or render `$-5.00` / `$NaN` in every reader this gates, and
 * the old readers (`cost > 0`, `a.cost &&`) already excluded both, so the one
 * predicate keeps that rather than leaving each call site to (JAR-2533 review).
 */
export const stopIsPriced = (cost: number | undefined): cost is number => cost != null && Number.isFinite(cost) && cost >= 0;

export function activityCostState(a: { cost?: number; booked?: boolean }): CostState {
  if (!stopIsPriced(a.cost)) return 'free';
  if (a.booked) return 'booked';
  return 'estimated';
}

/**
 * Round a set of amounts to whole dollars so they still add up to `total`.
 *
 * Money is displayed in whole dollars app-wide (JAR-288), and rounding each row
 * on its own lets the error accumulate: six categories dividing $8,000 exactly
 * rendered as 2,204 + 2,343 + 1,441 + 1,246 + 432 + 335 = $8,001. On a screen
 * whose whole purpose is showing how a total is divided, a column that does not
 * add up reads as a broken feature — the underlying cents were correct.
 *
 * Largest-remainder: floor everything, then hand the leftover dollars to the
 * amounts that lost the most in the rounding. Ties break on index so the same
 * preview always renders the same way.
 */
export function apportionWholeDollars(amounts: number[], total: number): number[] {
  const target = Math.round(total);
  const floors = amounts.map((a) => Math.floor(a));
  let leftover = target - floors.reduce((sum, v) => sum + v, 0);

  const order = amounts
    .map((a, idx) => ({ idx, frac: a - Math.floor(a) }))
    .sort((a, b) => b.frac - a.frac || a.idx - b.idx);

  const out = [...floors];
  // `leftover` can be negative when the amounts overshoot the total (a caller
  // passing values that do not sum to it); take from the smallest fractions
  // rather than silently rendering a column that still does not add up.
  for (let i = 0; leftover > 0 && i < order.length; i++, leftover--) out[order[i].idx]++;
  for (let i = order.length - 1; leftover < 0 && i >= 0; i--, leftover++) out[order[i].idx]--;

  return out;
}

/**
 * The budget with a recorded expense replaced by `next`, or deleted when `next`
 * is null (JAR-2048). The old amount comes off its category and the totals and
 * the new one goes on. Remaining is the total less what's spent, worked out
 * again rather than moved, since a stored remaining stops at zero once a budget
 * is overspent. `prev` names the row, and the change is measured from the row as
 * the budget holds it now, which a read-back while the form was open may have
 * changed. A row the budget no longer holds leaves it as it is.
 */
export function replaceExpense(budget: Budget, prev: Transaction, next: Transaction | null): Budget {
  const current = budget.transactions.find((t) => t.id === prev.id);
  if (!current) return budget;
  const delta = (next?.amount ?? 0) - current.amount;
  return {
    ...budget,
    spent: budget.spent + delta,
    remaining: Math.max(0, budget.total - (budget.spent + delta)),
    categories: budget.categories.map((c) => ({
      ...c,
      spent: c.spent - (c.id === current.category ? current.amount : 0) + (next && c.id === next.category ? next.amount : 0),
    })),
    transactions: next
      ? budget.transactions.map((t) => (t.id === prev.id ? next : t))
      : budget.transactions.filter((t) => t.id !== prev.id),
  };
}
